import { useUpdateScimEnablement } from '@tinct/core-api';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';

type ScimEnablementModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The state being confirmed: `true` switches SCIM provisioning on, `false` switches it off. */
  enabled: boolean;
};

/**
 * Confirms switching SCIM provisioning on or off. Surface-agnostic (no workspace hooks, the
 * workspace comes in as `organizationId`), like `MfaRequirementModal`.
 */
export const ScimEnablementModal = ({
  open,
  onOpenChange,
  organizationId,
  enabled,
}: ScimEnablementModalProps) => {
  const updateEnablement = useUpdateScimEnablement({
    onSuccess: () => {
      toast.success(
        enabled
          ? 'SCIM provisioning is switched on'
          : 'SCIM provisioning is switched off',
      );
      onOpenChange(false);
    },
  });

  // The API is the gate: 409 (5701) names the prerequisite that is missing. The dialog stays open
  // with that message rather than closing on a toast.
  const refusal = updateEnablement.error?.error ?? null;
  const isSwitching = updateEnablement.isPending;

  return (
    // Held open while the switch is applied, so that its outcome (toast or refusal) is what the admin sees
    <Dialog open={open} onOpenChange={(next) => (next || !isSwitching) && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {enabled
              ? 'Switch on SCIM provisioning?'
              : 'Switch off SCIM provisioning?'}
          </DialogTitle>
          <DialogDescription>
            {enabled
              ? 'Your identity provider will be able to manage the members of this workspace, calling the base URL with one of the tokens of this card.'
              : 'Nobody is deprovisioned: members keep their accounts and their access. Your identity provider’s requests are refused, and its tokens stop working until provisioning is switched on again.'}
          </DialogDescription>
        </DialogHeader>

        {refusal && (
          <Alert variant="important">
            <TriangleAlertIcon />
            <AlertDescription>{refusal}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSwitching}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={enabled ? 'default' : 'destructive'}
            onClick={() =>
              updateEnablement.mutate({ organizationId, enabled })
            }
            isLoading={isSwitching}
          >
            {enabled ? 'Switch on' : 'Switch off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
